import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useBrandManager } from '@/hooks/useBrandManager';

export interface PerformanceBriefPrompt {
  id: string;
  brand_id: string;
  prompt_type: string;
  name: string;
  prompt: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreatePromptData {
  name: string;
  prompt: string;
  prompt_type?: string;
  is_default?: boolean;
}

export const usePerformanceBriefPrompts = () => {
  const [prompts, setPrompts] = useState<PerformanceBriefPrompt[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { currentBrandId } = useBrandManager();
  const { toast } = useToast();

  const fetchPrompts = useCallback(async () => {
    if (!currentBrandId) {
      setPrompts([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('os_performance_brief_prompts')
        .select('*')
        .eq('brand_id', currentBrandId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPrompts((data as PerformanceBriefPrompt[]) || []);
    } catch (err) {
      console.error('Error fetching prompts:', err);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load prompts',
      });
    } finally {
      setIsLoading(false);
    }
  }, [currentBrandId, toast]);

  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts]);

  const createPrompt = async (data: CreatePromptData) => {
    if (!currentBrandId) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'No brand selected',
      });
      return null;
    }

    setIsSaving(true);
    try {
      const { data: newPrompt, error } = await supabase
        .from('os_performance_brief_prompts')
        .insert({
          brand_id: currentBrandId,
          name: data.name,
          prompt: data.prompt,
          prompt_type: data.prompt_type || 'generate_brief',
          is_default: data.is_default || false,
        })
        .select()
        .single();

      if (error) throw error;

      setPrompts(prev => [newPrompt as PerformanceBriefPrompt, ...prev]);
      toast({
        title: 'Success',
        description: 'Prompt created successfully',
      });
      return newPrompt as PerformanceBriefPrompt;
    } catch (err) {
      console.error('Error creating prompt:', err);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to create prompt',
      });
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  const updatePrompt = async (id: string, data: Partial<CreatePromptData>) => {
    setIsSaving(true);
    try {
      const { data: updatedPrompt, error } = await supabase
        .from('os_performance_brief_prompts')
        .update({
          ...data,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      setPrompts(prev => prev.map(p => p.id === id ? (updatedPrompt as PerformanceBriefPrompt) : p));
      toast({
        title: 'Success',
        description: 'Prompt updated successfully',
      });
      return updatedPrompt as PerformanceBriefPrompt;
    } catch (err) {
      console.error('Error updating prompt:', err);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to update prompt',
      });
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  const deletePrompt = async (id: string) => {
    try {
      const { error } = await supabase
        .from('os_performance_brief_prompts')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setPrompts(prev => prev.filter(p => p.id !== id));
      toast({
        title: 'Success',
        description: 'Prompt deleted successfully',
      });
      return true;
    } catch (err) {
      console.error('Error deleting prompt:', err);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to delete prompt',
      });
      return false;
    }
  };

  const duplicatePrompt = async (id: string) => {
    const originalPrompt = prompts.find(p => p.id === id);
    if (!originalPrompt) return null;

    return createPrompt({
      name: `${originalPrompt.name} (Copy)`,
      prompt: originalPrompt.prompt,
      prompt_type: originalPrompt.prompt_type,
      is_default: false,
    });
  };

  const getDefaultPrompt = () => {
    return prompts.find(p => p.is_default) || prompts[0] || null;
  };

  const getPromptsByType = (promptType: string) => {
    return prompts.filter(p => p.prompt_type === promptType);
  };

  const getDefaultPromptByType = (promptType: string) => {
    const typePrompts = getPromptsByType(promptType);
    return typePrompts.find(p => p.is_default) || typePrompts[0] || null;
  };

  return {
    prompts,
    isLoading,
    isSaving,
    fetchPrompts,
    createPrompt,
    updatePrompt,
    deletePrompt,
    duplicatePrompt,
    getDefaultPrompt,
    getPromptsByType,
    getDefaultPromptByType,
  };
};
